import { SearchableSelect } from '@/components/ui/searchable-select'
import type { AuthConfigCopy, ComposioIntegrationRow } from '@/frontend/pages/CommandCenter/Integrations/types'

interface Props {
  integration: ComposioIntegrationRow
  copy: AuthConfigCopy
  configsLoaded: boolean
  disabled: boolean
  onChange: (authConfigId: string) => void
}

function selectionStatus(
  integration: ComposioIntegrationRow,
  copy: AuthConfigCopy,
  configsLoaded: boolean,
  selectionMissing: boolean,
) {
  if (!configsLoaded || selectionMissing) return copy.unavailable

  const selectedConfig = integration.authConfigs.find((config) => config.id === integration.selectedAuthConfigId)
  if (selectedConfig) return `${copy.selected}: ${selectedConfig.name}`
  if (integration.authConfigs.length === 0) return copy.empty

  return integration.authSchemes.join(', ')
}

export default function AuthConfigSelect({ integration, copy, configsLoaded, disabled, onChange }: Props) {
  const selectedId = integration.selectedAuthConfigId ?? ''
  const selectedConfig = integration.authConfigs.find((config) => config.id === selectedId)
  const selectionMissing = selectedId !== '' && !selectedConfig
  const options = integration.authConfigs.map((config) => ({
    value: config.id,
    label: `${config.name} · ${config.authScheme ?? '—'} · ${config.id}`,
  }))

  if (selectionMissing) {
    options.unshift({ value: selectedId, label: `${copy.unavailable} · ${selectedId}` })
  }

  if (integration.noAuth) {
    return <span className="text-sm text-muted-foreground">{integration.authSchemes.join(', ') || '—'}</span>
  }

  const selectorDisabled = disabled || !configsLoaded || (integration.authConfigs.length === 0 && !selectionMissing)
  const status = selectionStatus(integration, copy, configsLoaded, selectionMissing)

  return (
    <div className="w-[330px] max-w-full space-y-1.5">
      <SearchableSelect
        options={options}
        value={selectedId}
        onValueChange={onChange}
        placeholder={copy.automatic}
        searchPlaceholder={copy.searchPlaceholder}
        ariaLabel={`${copy.selectLabel} ${integration.name}`}
        clearLabel={copy.automatic}
        allowClear={!integration.authConfigRequired}
        emptyStateLabel={copy.empty}
        disabled={selectorDisabled}
        className="w-full"
      />
      <div className="flex min-w-0 items-center gap-2 px-0.5 text-[11px] text-muted-foreground">
        <span className="truncate">{status}</span>
        {selectedId && <code className="ml-auto shrink-0 text-[10px]">{selectedId}</code>}
      </div>
    </div>
  )
}
